import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Snackbar, 
  Alert, 
  Stepper,
  Step,
  StepLabel,
  Box,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, CalendarToday } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { styled } from '@mui/material/styles';
import ThemeBackground from '../../components/Layout/ThemeBackground';
import { uploadFile } from '../../services/uploadService';
import { fetchPublicLookups } from '../../api/lookups';
import { Lookup } from '../../types/lookup';
import AvailabilitySlotEditor, { AvailabilitySlot } from '../../components/shared/AvailabilitySlotEditor';
import api from '../../utils/api';
import { toDateOnlyString } from '../../utils/dateUtils';
import '../../index.css';

const DatePickerWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  '& .react-datepicker-popper': {
    zIndex: 9999,
  },
  '& .react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    zIndex: 9999,
  },
  '& .react-datepicker__header': {
    backgroundColor: theme.palette.grey[100],
  },
  '& .react-datepicker__day--selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block'
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '8px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '4px',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main
    }
  }
}));

const steps = ['Personal Info', 'Professional Info', 'Qualifications & Supervisor', 'Legal Documents'];

const nationalities = [
  'British', 'Irish', 'Other EU', 'American', 'Canadian', 'Australian', 
  'New Zealand', 'South African', 'Indian', 'Pakistani', 'Bangladeshi', 'Other'
];

const genderOptions = ['Male', 'Female', 'Non-binary', 'Prefer not to say', 'Other'];

interface CounsellorInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dob?: Date;
  completionDeadline?: Date;
}

interface FormData {
  phone: string;
  dob: string;
  gender: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  county: string;
  postcode: string;
  modality: string[];
  sessionTypes: string[];
  availability: string[];
  availabilitySlots: AvailabilitySlot[];
  experienceOrTraining: string[];
  languages: string[];
  supervisorName: string;
  supervisorPhone: string;
  bacpNumber: string;
  qualified: boolean;
  qualificationDate?: string;
  qualificationCertificate?: { name: string; url: string; originalName: string; };
  exitDate?: string;
  nationality: string;
  workPermit: boolean;
  workPermitDetails?: string;
  workPermitDocument?: { name: string; url: string; originalName: string; };
  dbsCheck: boolean;
  dbsType?: 'certificate' | 'update_service';
  dbsNumber: string;
  dbsIssueDate?: string; // Issue/print date from certificate
  dbsExpiryDate?: string; // Calculated from issue date (3 years)
  dbsUpdateServiceId?: string;
  dbsUpdateServiceConsent?: boolean;
  dbsCertificate?: { name: string; url: string; originalName: string; };
  insuranceProvider: string;
  insuranceNumber: string;
  insuranceExpiryDate: string;
  insuranceCertificate?: { name: string; url: string; originalName: string; };
  onlineLearningCertificate?: { name: string; url: string; originalName: string; };
}

const CompleteProfile: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [counsellorInfo, setCounsellorInfo] = useState<CounsellorInfo | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | undefined>();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const [formData, setFormData] = useState<FormData>({
    phone: '',
    dob: '',
    gender: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    county: '',
    postcode: '',
    modality: [],
    sessionTypes: [],
    availability: [],
    availabilitySlots: [],
    experienceOrTraining: [],
    languages: [],
    supervisorName: '',
    supervisorPhone: '',
    bacpNumber: '',
    qualified: false,
    nationality: '',
    workPermit: false,
    dbsCheck: false,
    dbsNumber: '',
    insuranceProvider: '',
    insuranceNumber: '',
    insuranceExpiryDate: ''
  });

  const [modalityLookups, setModalityLookups] = useState<Lookup[]>([]);
  const [sessionTypesLookups, setSessionTypesLookups] = useState<Lookup[]>([]);
  const [availabilityLookups, setAvailabilityLookups] = useState<Lookup[]>([]);
  const [experienceLookups, setExperienceLookups] = useState<Lookup[]>([]);
  const [languageLookups, setLanguageLookups] = useState<Lookup[]>([]);

  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');

  const months = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 18 - i);

  // Verify token and load counsellor info
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await api.get(`/api/onboarding/${token}`);
        const counsellor = response.data.counsellor;
        setCounsellorInfo(counsellor);
        
        // Pre-fill phone if provided by admin
        if (counsellor.phone) {
          setFormData(prev => ({ ...prev, phone: counsellor.phone }));
        }
        
        // Pre-fill DOB if provided by admin
        if (counsellor.dob) {
          const dobDate = new Date(counsellor.dob);
          setDay(String(dobDate.getDate()).padStart(2, '0'));
          setMonth(String(dobDate.getMonth() + 1).padStart(2, '0'));
          setYear(String(dobDate.getFullYear()));
          setFormData(prev => ({ ...prev, dob: counsellor.dob }));
        }
        
        setLoading(false);
      } catch (error: any) {
        setApiError(error.response?.data?.error || 'Invalid or expired link');
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    }
  }, [token]);

  // Load lookups
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [modality, sessionTypes, availability, experience, languages] = await Promise.all([
          fetchPublicLookups('modality'),
          fetchPublicLookups('sessionTypes'),
          fetchPublicLookups('availability'),
          fetchPublicLookups('experienceTraining'),
          fetchPublicLookups('language')
        ]);
        setModalityLookups(modality);
        setSessionTypesLookups(sessionTypes);
        setAvailabilityLookups(availability);
        setExperienceLookups(experience);
        setLanguageLookups(languages);
      } catch (error) {
        console.error('Error loading lookups:', error);
      }
    };
    loadLookups();
  }, []);

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = async (file: File, field: keyof FormData) => {
    setUploading(true);
    try {
      // Use onboarding-specific upload endpoint
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/onboarding/${token}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploadedFile = response.data;
      handleChange(field, uploadedFile);
      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Failed to upload file',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      // Phone validation
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone is required';
      } else {
        // UK phone number validation - accepts various formats
        const phoneRegex = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;
        const cleanPhone = formData.phone.replace(/\s/g, '');
        const ukMobileRegex = /^(\+447\d{9}|07\d{9})$/;
        
        if (!ukMobileRegex.test(cleanPhone)) {
          newErrors.phone = 'Please enter a valid UK mobile number (e.g., 07123456789 or +447123456789)';
        }
      }
      
      if (!day || !month || !year) newErrors.dob = 'Date of birth is required';
      if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.county.trim()) newErrors.county = 'County is required';
      
      // Postcode validation
      if (!formData.postcode.trim()) {
        newErrors.postcode = 'Postcode is required';
      } else {
        // UK postcode validation - comprehensive regex
        const postcodeRegex = /^([A-Z]{1,2}\d{1,2}[A-Z]?)\s?(\d[A-Z]{2})$/i;
        const cleanPostcode = formData.postcode.trim().toUpperCase();
        
        if (!postcodeRegex.test(cleanPostcode)) {
          newErrors.postcode = 'Please enter a valid UK postcode (e.g., SW1A 1AA or M1 1AE)';
        }
      }
    } else if (step === 1) {
      if (formData.modality.length === 0) newErrors.modality = 'At least one modality is required';
      if (formData.sessionTypes.length === 0) newErrors.sessionTypes = 'At least one session type is required';
    } else if (step === 2) {
      if (!formData.supervisorName.trim()) newErrors.supervisorName = 'Clinical supervisor name is required';
      
      // Supervisor phone validation
      if (!formData.supervisorPhone.trim()) {
        newErrors.supervisorPhone = 'Clinical supervisor phone is required';
      } else {
        const cleanPhone = formData.supervisorPhone.replace(/\s/g, '');
        const ukMobileRegex = /^(\+447\d{9}|07\d{9})$/;
        
        if (!ukMobileRegex.test(cleanPhone)) {
          newErrors.supervisorPhone = 'Please enter a valid UK mobile number (e.g., 07123456789 or +447123456789)';
        }
      }
      
      if (!formData.bacpNumber.trim()) newErrors.bacpNumber = 'BACP/NCPS registration number is required';
      
      if (formData.qualified) {
        if (!formData.qualificationDate) {
          newErrors.qualificationDate = 'Qualification date is required';
        } else {
          const qualDate = new Date(formData.qualificationDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (qualDate >= today) {
            newErrors.qualificationDate = 'Qualification date must be in the past';
          }
        }
        if (!formData.qualificationCertificate) {
          newErrors.qualificationCertificate = 'Qualification certificate is required';
        }
      }
      
      if (formData.exitDate) {
        const exitDate = new Date(formData.exitDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (exitDate <= today) {
          newErrors.exitDate = 'Exit date must be in the future';
        }
      }
    } else if (step === 3) {
      if (formData.dbsCheck) {
        if (!formData.dbsNumber) newErrors.dbsNumber = 'DBS number is required';
        if (formData.dbsType === 'certificate' && !formData.dbsCertificate) {
          newErrors.dbsCertificate = 'DBS certificate is required';
        }
        if (formData.dbsType === 'update_service' && !formData.dbsUpdateServiceId) {
          newErrors.dbsUpdateServiceId = 'DBS Update Service ID is required';
        }
        if (formData.dbsType === 'certificate' && formData.dbsIssueDate) {
          const issueDate = new Date(formData.dbsIssueDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (issueDate > today) {
            newErrors.dbsIssueDate = 'DBS issue date cannot be in the future';
          }
        }
      }
      if (!formData.insuranceCertificate) newErrors.insuranceCertificate = 'Insurance certificate is required';
      if (!formData.insuranceProvider) newErrors.insuranceProvider = 'Insurance provider is required';
      if (!formData.insuranceNumber) newErrors.insuranceNumber = 'Insurance number is required';
      if (!formData.insuranceExpiryDate) {
        newErrors.insuranceExpiryDate = 'Insurance expiry date is required';
      } else {
        const insuranceExpiry = new Date(formData.insuranceExpiryDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (insuranceExpiry <= today) {
          newErrors.insuranceExpiryDate = 'Insurance expiry date must be in the future';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setSubmitting(true);
    setApiError(undefined);

    try {
      const formattedDob = `${year}-${month}-${day}`;
      
      // Derive DBS flags from provided data
      const hasDBS = !!(formData.dbsNumber && formData.dbsType);
      
      const payload = {
        ...formData,
        dob: formattedDob,
        dbsCheck: hasDBS
        // dbsUpdateServiceConsent is already in formData from the checkbox, don't overwrite it
      };

      await api.put(`/api/onboarding/${token}`, payload);
      
      setSnackbar({
        open: true,
        message: 'Profile submitted successfully! Our admin team will review your information.',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error: any) {
      setApiError(error.response?.data?.error || 'Failed to submit profile');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeBackground>
    );
  }

  if (apiError && !counsellorInfo) {
    return (
      <ThemeBackground variant="full">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <Alert severity="error">{apiError}</Alert>
        </Box>
      </ThemeBackground>
    );
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold theme-text-primary mb-6">Personal Information</h3>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="theme-input w-full"
                required
              />
              {errors.phone && <p className="mt-1 text-sm text-red-400">{errors.phone}</p>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className="theme-input w-full"
                >
                  <option value="">Select gender</option>
                  {genderOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Date of Birth *</label>
                <div className="flex gap-2">
                  <select
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    className="theme-input flex-1"
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="theme-input flex-1"
                  >
                    <option value="">Month</option>
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="theme-input flex-1"
                  >
                    <option value="">Year</option>
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                {errors.dob && <p className="mt-1 text-sm text-red-400">{errors.dob}</p>}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Address Line 1 *</label>
              <input
                type="text"
                value={formData.addressLine1}
                onChange={(e) => handleChange('addressLine1', e.target.value)}
                className="theme-input w-full"
                required
              />
              {errors.addressLine1 && <p className="mt-1 text-sm text-red-400">{errors.addressLine1}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Address Line 2</label>
              <input
                type="text"
                value={formData.addressLine2}
                onChange={(e) => handleChange('addressLine2', e.target.value)}
                className="theme-input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Address Line 3</label>
              <input
                type="text"
                value={formData.addressLine3}
                onChange={(e) => handleChange('addressLine3', e.target.value)}
                className="theme-input w-full"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="theme-input w-full"
                  required
                />
                {errors.city && <p className="mt-1 text-sm text-red-400">{errors.city}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">County *</label>
                <input
                  type="text"
                  value={formData.county}
                  onChange={(e) => handleChange('county', e.target.value)}
                  className="theme-input w-full"
                  required
                />
                {errors.county && <p className="mt-1 text-sm text-red-400">{errors.county}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Postcode *</label>
                <input
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => handleChange('postcode', e.target.value)}
                  className="theme-input w-full"
                  required
                />
                {errors.postcode && <p className="mt-1 text-sm text-red-400">{errors.postcode}</p>}
              </div>
            </div>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold theme-text-primary mb-6">Professional Information</h3>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Modality *</label>
              <FormControl fullWidth required error={!!errors.modality}>
                <Select
                  multiple
                  value={formData.modality}
                  onChange={(e) => handleChange('modality', e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const lookup = modalityLookups.find(l => l.value === value);
                        return <Chip key={value} label={lookup?.displayName || value} size="small" sx={{ color: 'white', bgcolor: 'rgba(99, 102, 241, 0.3)' }} />;
                      })}
                    </Box>
                  )}
                  className="theme-input"
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e3a8a',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                          '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                        }
                      }
                    }
                  }}
                >
                  {modalityLookups.map((lookup) => (
                    <MenuItem key={lookup._id} value={lookup.value}>
                      {lookup.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.modality && <p className="mt-1 text-sm text-red-400">{errors.modality}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Session Types *</label>
              <FormControl fullWidth required error={!!errors.sessionTypes}>
                <Select
                  multiple
                  value={formData.sessionTypes}
                  onChange={(e) => handleChange('sessionTypes', e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const lookup = sessionTypesLookups.find(l => l.value === value);
                        return <Chip key={value} label={lookup?.displayName || value} size="small" sx={{ color: 'white', bgcolor: 'rgba(99, 102, 241, 0.3)' }} />;
                      })}
                    </Box>
                  )}
                  className="theme-input"
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e3a8a',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                          '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                        }
                      }
                    }
                  }}
                >
                  {sessionTypesLookups.map((lookup) => (
                    <MenuItem key={lookup._id} value={lookup.value}>
                      {lookup.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.sessionTypes && <p className="mt-1 text-sm text-red-400">{errors.sessionTypes}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Availability Slots</label>
              <AvailabilitySlotEditor
                slots={formData.availabilitySlots}
                onChange={(slots) => handleChange('availabilitySlots', slots)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Experience/Training</label>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={formData.experienceOrTraining}
                  onChange={(e) => handleChange('experienceOrTraining', e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const lookup = experienceLookups.find(l => l.value === value);
                        return <Chip key={value} label={lookup?.displayName || value} size="small" sx={{ color: 'white', bgcolor: 'rgba(99, 102, 241, 0.3)' }} />;
                      })}
                    </Box>
                  )}
                  className="theme-input"
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e3a8a',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                          '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                        }
                      }
                    }
                  }}
                >
                  {experienceLookups.map((lookup) => (
                    <MenuItem key={lookup._id} value={lookup.value}>
                      {lookup.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Languages</label>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={formData.languages}
                  onChange={(e) => handleChange('languages', e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const lookup = languageLookups.find(l => l.value === value);
                        return <Chip key={value} label={lookup?.displayName || value} size="small" sx={{ color: 'white', bgcolor: 'rgba(99, 102, 241, 0.3)' }} />;
                      })}
                    </Box>
                  )}
                  className="theme-input"
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e3a8a',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                          '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                        }
                      }
                    }
                  }}
                >
                  {languageLookups.map((lookup) => (
                    <MenuItem key={lookup._id} value={lookup.value}>
                      {lookup.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold theme-text-primary mb-6">Qualifications & Supervisor</h3>
            
            <h4 className="text-md font-semibold theme-text-primary mt-6 mb-4">Clinical Supervisor Details *</h4>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Supervisor Name *</label>
              <input
                type="text"
                value={formData.supervisorName}
                onChange={(e) => handleChange('supervisorName', e.target.value)}
                className="theme-input w-full"
                required
              />
              {errors.supervisorName && <p className="mt-1 text-sm text-red-400">{errors.supervisorName}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Supervisor Phone *</label>
              <input
                type="tel"
                value={formData.supervisorPhone}
                onChange={(e) => handleChange('supervisorPhone', e.target.value)}
                className="theme-input w-full"
                required
              />
              {errors.supervisorPhone && <p className="mt-1 text-sm text-red-400">{errors.supervisorPhone}</p>}
            </div>
            
            <h4 className="text-md font-semibold theme-text-primary mt-6 mb-4">Professional Registration *</h4>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">BACP/NCPS Registration Number *</label>
              <input
                type="text"
                value={formData.bacpNumber}
                onChange={(e) => handleChange('bacpNumber', e.target.value)}
                className="theme-input w-full"
                placeholder="Enter your BACP or NCPS registration number"
                required
              />
              {errors.bacpNumber && <p className="mt-1 text-sm text-red-400">{errors.bacpNumber}</p>}
            </div>
            
            <h4 className="text-md font-semibold theme-text-primary mt-6 mb-4">Qualification Status</h4>
            
            <div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.qualified}
                  onChange={(e) => handleChange('qualified', e.target.checked)}
                  className="mr-2 w-4 h-4"
                />
                <span className="text-sm theme-text-primary">I am qualified</span>
              </label>
            </div>
            
            {formData.qualified && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">Qualification Date *</label>
                  <div className="relative">
                    <DatePicker
                      selected={formData.qualificationDate ? new Date(formData.qualificationDate) : null}
                      onChange={(date: Date | null) => {
                        if (date) {
                          handleChange('qualificationDate', toDateOnlyString(date));
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      className="theme-input w-full p-2 pr-10 border rounded-lg"
                      placeholderText="Select qualification date"
                      popperClassName="datepicker-popper"
                      popperPlacement="bottom-start"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                    <CalendarToday
                      onClick={() => {
                        const input = document.querySelector('.theme-input[placeholder="Select qualification date"]') as HTMLInputElement;
                        if (input) input.focus();
                      }}
                      sx={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        color: 'rgba(255, 255, 255, 0.7)',
                        pointerEvents: 'auto',
                        '&:hover': { color: 'white' }
                      }}
                    />
                  </div>
                  {errors.qualificationDate && <p className="mt-1 text-sm text-red-400">{errors.qualificationDate}</p>}
                </div>
                
                <div>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                    sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                  >
                    Upload Qualification Certificate
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'qualificationCertificate');
                      }}
                    />
                  </Button>
                  {formData.qualificationCertificate && (
                    <p className="mt-2 text-sm theme-text-primary">{formData.qualificationCertificate.originalName}</p>
                  )}
                  {errors.qualificationCertificate && <p className="mt-1 text-sm text-red-400">{errors.qualificationCertificate}</p>}
                </div>
              </div>
            )}
            
            <h4 className="text-md font-semibold theme-text-primary mt-6 mb-4">Exit Date (Optional)</h4>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Do you have an exit date with Evolving Minds?</label>
              <div className="relative">
                <DatePicker
                  selected={formData.exitDate ? new Date(formData.exitDate) : null}
                  onChange={(date: Date | null) => {
                    if (date) {
                      handleChange('exitDate', toDateOnlyString(date));
                    } else {
                      handleChange('exitDate', undefined);
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  className="theme-input w-full p-2 pr-10 border rounded-lg"
                  placeholderText="Select exit date (if applicable)"
                  popperClassName="datepicker-popper"
                  popperPlacement="bottom-start"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  isClearable
                />
                <CalendarToday
                  onClick={() => {
                    const input = document.querySelector('.theme-input[placeholder="Select exit date (if applicable)"]') as HTMLInputElement;
                    if (input) input.focus();
                  }}
                  sx={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: 'rgba(255, 255, 255, 0.7)',
                    pointerEvents: 'auto',
                    '&:hover': { color: 'white' }
                  }}
                />
              </div>
              {errors.exitDate && <p className="mt-1 text-sm text-red-400">{errors.exitDate}</p>}
              <p className="mt-1 text-xs theme-text-primary opacity-70">Leave blank if you don't have a planned exit date</p>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold theme-text-primary mb-6">Legal Documents</h3>
            
            {/* DBS Section - Mandatory */}
            <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">DBS Type</label>
                  <select
                    value={formData.dbsType || ''}
                    onChange={(e) => handleChange('dbsType', e.target.value)}
                    className="theme-input w-full"
                  >
                    <option value="">Select DBS type</option>
                    <option value="certificate">Certificate</option>
                    <option value="update_service">Update Service</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">DBS Number *</label>
                  <input
                    type="text"
                    value={formData.dbsNumber}
                    onChange={(e) => handleChange('dbsNumber', e.target.value)}
                    className="theme-input w-full"
                    required
                  />
                  {errors.dbsNumber && <p className="mt-1 text-sm text-red-400">{errors.dbsNumber}</p>}
                </div>
                
                {formData.dbsType === 'certificate' && (
                  <>
                    <div>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploading}
                        sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                      >
                        Upload DBS Certificate
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(file, 'dbsCertificate');
                          }}
                        />
                      </Button>
                      {formData.dbsCertificate && (
                        <p className="mt-2 text-sm theme-text-primary">{formData.dbsCertificate.originalName}</p>
                      )}
                      {errors.dbsCertificate && <p className="mt-1 text-sm text-red-400">{errors.dbsCertificate}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium theme-text-primary mb-2">DBS Issue Date</label>
                      <div className="relative">
                        <DatePicker
                          selected={formData.dbsIssueDate ? new Date(formData.dbsIssueDate) : null}
                          onChange={(date: Date | null) => {
                            if (date) {
                              handleChange('dbsIssueDate', toDateOnlyString(date));
                            }
                          }}
                          dateFormat="dd/MM/yyyy"
                          className="theme-input w-full p-2 pr-10 border rounded-lg"
                          placeholderText="Select issue date (from certificate)"
                          maxDate={new Date()}
                          popperClassName="datepicker-popper"
                          popperPlacement="bottom-start"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                        />
                        <CalendarToday
                          onClick={() => {
                            const input = document.querySelector('.theme-input[placeholder="Select expiry date"]') as HTMLInputElement;
                            if (input) input.focus();
                          }}
                          sx={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            color: 'rgba(255, 255, 255, 0.7)',
                            pointerEvents: 'auto',
                            '&:hover': { color: 'white' }
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                
                {formData.dbsType === 'update_service' && (
                  <div>
                    <label className="block text-sm font-medium theme-text-primary mb-2">DBS Update Service ID *</label>
                    <input
                      type="text"
                      value={formData.dbsUpdateServiceId || ''}
                      onChange={(e) => handleChange('dbsUpdateServiceId', e.target.value)}
                      className="theme-input w-full"
                      required
                    />
                    {errors.dbsUpdateServiceId && <p className="mt-1 text-sm text-red-400">{errors.dbsUpdateServiceId}</p>}
                  </div>
                )}
              </div>
            
            <h4 className="text-md font-semibold theme-text-primary mt-6 mb-4">Professional Indemnity Insurance *</h4>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Insurance Provider *</label>
              <input
                type="text"
                value={formData.insuranceProvider}
                onChange={(e) => handleChange('insuranceProvider', e.target.value)}
                className="theme-input w-full"
                required
              />
              {errors.insuranceProvider && <p className="mt-1 text-sm text-red-400">{errors.insuranceProvider}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Insurance Number *</label>
              <input
                type="text"
                value={formData.insuranceNumber}
                onChange={(e) => handleChange('insuranceNumber', e.target.value)}
                className="theme-input w-full"
                required
              />
              {errors.insuranceNumber && <p className="mt-1 text-sm text-red-400">{errors.insuranceNumber}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Insurance Expiry Date *</label>
              <div className="relative">
                <DatePicker
                  selected={formData.insuranceExpiryDate ? new Date(formData.insuranceExpiryDate) : null}
                  onChange={(date: Date | null) => {
                    if (date) {
                      handleChange('insuranceExpiryDate', toDateOnlyString(date));
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  className="theme-input w-full p-2 pr-10 border rounded-lg"
                  placeholderText="Select expiry date"
                  popperClassName="datepicker-popper"
                  popperPlacement="bottom-start"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
                <CalendarToday
                  onClick={() => {
                    const inputs = document.querySelectorAll('.theme-input[placeholder="Select expiry date"]');
                    const insuranceInput = inputs[inputs.length - 1] as HTMLInputElement;
                    if (insuranceInput) insuranceInput.focus();
                  }}
                  sx={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: 'rgba(255, 255, 255, 0.7)',
                    pointerEvents: 'auto',
                    '&:hover': { color: 'white' }
                  }}
                />
              </div>
              {errors.insuranceExpiryDate && <p className="mt-1 text-sm text-red-400">{errors.insuranceExpiryDate}</p>}
            </div>
            
            <div>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
                sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
              >
                Upload Insurance Certificate
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'insuranceCertificate');
                  }}
                />
              </Button>
              {formData.insuranceCertificate && (
                <p className="mt-2 text-sm theme-text-primary">{formData.insuranceCertificate.originalName}</p>
              )}
              {errors.insuranceCertificate && <p className="mt-1 text-sm text-red-400">{errors.insuranceCertificate}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Nationality</label>
              <select
                value={formData.nationality}
                onChange={(e) => handleChange('nationality', e.target.value)}
                className="theme-input w-full"
              >
                <option value="">Select nationality</option>
                {nationalities.map(nat => (
                  <option key={nat} value={nat}>{nat}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.workPermit}
                  onChange={(e) => handleChange('workPermit', e.target.checked)}
                  className="mr-2 w-4 h-4"
                />
                <span className="text-sm theme-text-primary">Work Permit Required</span>
              </label>
            </div>
            
            {formData.workPermit && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">Work Permit Details</label>
                  <textarea
                    value={formData.workPermitDetails || ''}
                    onChange={(e) => handleChange('workPermitDetails', e.target.value)}
                    rows={3}
                    className="theme-input w-full"
                  />
                </div>
                
                <div>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                    sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                  >
                    Upload Work Permit Document
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'workPermitDocument');
                      }}
                    />
                  </Button>
                  {formData.workPermitDocument && (
                    <p className="mt-2 text-sm theme-text-primary">{formData.workPermitDocument.originalName}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, mb: 2, color: 'white' }}>
              Complete Your Counsellor Profile
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'white' }}>
              Welcome {counsellorInfo?.firstName}! Please complete your profile by {counsellorInfo?.completionDeadline ? new Date(counsellorInfo.completionDeadline).toLocaleDateString() : 'the deadline'}.
            </Typography>

            <Stepper activeStep={activeStep} sx={{ 
              mb: 4,
              '& .MuiStepLabel-label': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiStepLabel-label.Mui-active': { color: 'white' },
              '& .MuiStepLabel-label.Mui-completed': { color: 'white' },
              '& .MuiStepIcon-root': { color: 'rgba(255, 255, 255, 0.3)' },
              '& .MuiStepIcon-root.Mui-active': { color: 'white' },
              '& .MuiStepIcon-root.Mui-completed': { color: 'white' }
            }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {apiError && (
              <Alert severity="error" sx={{ mb: 3 }}>{apiError}</Alert>
            )}

            <Box sx={{ mt: 4 }}>
              {renderStepContent(activeStep)}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                Back
              </Button>
              <Box sx={{ flex: '1 1 auto' }} />
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Profile'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
};

export default CompleteProfile;
